import {Module} from 'vuex';
import ApiService from '../../services/api.service';
import type {RootState} from '..';

export interface PhoneMatchInsurance {
  payer: string | null;
  plan_name: string | null;
  member_id: string | null;
}

export interface PhoneMatch {
  uuid: string;
  name: string;
  avatar_url: string | null;
  external_id: number | null;
  chart_id: string | null;
  status: string | null;
  phonenumber: string | null;
  date_of_birth: string | null;
  insurance: PhoneMatchInsurance | null;
  offices: string[];
  last_visit_at: string | null;
}

export interface PatientLookupError {
  status: number | null;
  message: string;
}

export interface PatientLookupState {
  patients: PhoneMatch[];
  truncated: boolean;
  error: PatientLookupError | null;
  sequence: number;
}

export const patientLookup: Module<PatientLookupState, RootState> = {
  namespaced: false,
  state: {
    patients: [],
    truncated: false,
    error: null,
    sequence: 0,
  },
  getters: {
    phoneMatches: (state) => state.patients,
    phoneMatchesTruncated: (state) => state.truncated,
    phoneLookupError: (state) => state.error,
  },
  mutations: {
    startPhoneLookup: (state) => {
      state.sequence += 1;
      state.patients = [];
      state.truncated = false;
      state.error = null;
    },
    setPhoneMatches: (state, {patients, truncated}: {patients: PhoneMatch[], truncated: boolean}) => {
      state.patients = patients;
      state.truncated = truncated;
      state.error = null;
    },
    setPhoneLookupError: (state, error: PatientLookupError) => {
      state.patients = [];
      state.truncated = false;
      state.error = error;
    },
  },
  actions: {
    /**
     * Resolves to false when a newer lookup started while this one was in
     * flight — the softphone puts the next caller in the same tab, and the
     * older response must not decide what the page shows.
     */
    async LOOKUP_PATIENTS_BY_PHONE({commit, state}, payload: {phone: string}): Promise<boolean> {
      commit('startPhoneLookup');
      const sequence = state.sequence;

      try {
        const {data} = await ApiService.customRequest({
          method: 'get',
          url: 'patients/lookup-by-phone',
          params: {phone: payload.phone},
        });

        if (sequence !== state.sequence) {
          return false;
        }

        commit('setPhoneMatches', {
          patients: data.patients ?? [],
          truncated: !!data.truncated,
        });
      } catch (e: any) {
        if (sequence !== state.sequence) {
          return false;
        }

        // tslint:disable-next-line:no-console
        console.error('LOOKUP_PATIENTS_BY_PHONE ERROR - ', e);
        commit('setPhoneLookupError', {
          status: e?.response?.status ?? null,
          message: e?.response?.data?.errors?.phone?.[0]
            ?? e?.response?.data?.message
            ?? '',
        });
      }

      return true;
    },
  },
};
